<template>
  <div>
    <h3>{{label}}</h3>
    <div class="jodit-editor__wrapper" v-on-clickaway="onBlur" @focusout="onChange" @click="onClick">
      <div class="jodit-editor__container">
        <textarea ref="editor" name="editor"></textarea>
        <div v-if="showTextCount" :class="messageClasses">
          <div v-if="minCharacters && maxCharacters">
            {{charsWritten}} characters (limit - min: {{minCharacters}} - max: {{maxCharacters}})
          </div>
          <div v-else-if="minCharacters">
            {{charsWritten}} characters (limit - min: {{minCharacters}})
          </div>
          <div v-else-if="maxCharacters">
            {{charsWritten}} characters (limit - max: {{maxCharacters}})
          </div>
          <div v-else>
            {{charsWritten}} characters
          </div>
        </div>
      </div>
      <div class="jodit-editor__error" v-if="error.$dirty && error.$invalid">{{errorMessages[0]}}</div>
    </div>
  </div>
</template>

<script>
import Jodit from '../../../../plugins/jodit/jodit.min.js'
import errorMessages from '@/modules/error-messages'
import { mixin as clickaway } from 'vue-clickaway'
import { isNil } from 'ramda'

export default {
  name: 'ContentEditor',
  mixins: [ clickaway ],
  components: {},
  props: {
    label: {
      type: String
    },
    /* don't needed */
    value: {
      type: String,
      default: ''
    },
    error: {
      type: Object,
      default: () => ({})
    },
    height: {
      type: String,
      default: '150px'
    },
    placeholder: {
      type: String,
      default: 'Start typing...'
    },
    minCharacters: {
      type: Number
    },
    maxCharacters: {
      type: Number
    },
    showTextCount: {
      type: Boolean
    }
  },
  data () {
    return {
      editor: {},
      charsWritten: 0,
      focused: false,
      firstTouchPerformed: false
    }
  },
  validations: {
    charsWritten: {
      valid: val => {
        return val > 5
      }
    }
  },
  computed: {
    hasValidationError () {
      return this.error && this.error.$invalid
    },
    errorMessages () {
      if (!this.hasValidationError) {
        return []
      }
      const errorParams = []
      if (this.error.$params !== undefined && Object.keys(this.error.$params).length) {
        for (const param in this.error.$params) {
          if (this.error.$params.hasOwnProperty(param) && this.error[param] === false) {
            errorParams.push(param)
          }
        }
      }
      return this.generateErrors(errorParams)
    },
    messageClasses () {
      return [
        'jodit-editor__message',
        {'jodit-editor__message--invalid': !this.isCharCountValid && !this.focused && this.firstTouchPerformed},
        {'jodit-editor__message--valid': this.isCharCountValid}
      ]
    },
    isCharCountValid () {
      if (!this.minCharacters && !this.maxCharacters) {
        return this.charsWritten >= 1
      } else if (this.minCharacters && this.maxCharacters) {
        return this.charsWritten >= this.minCharacters && this.charsWritten <= this.maxCharacters
      } else if (this.minCharacters) {
        return this.charsWritten >= this.minCharacters
      } else if (this.maxCharacters) {
        return this.charsWritten > 1 && this.charsWritten <= this.maxCharacters
      }
    }
  },
  watch: {
    value (newVal) {
      this.updateEditorValue(newVal)
    }
  },
  methods: {
    onChange () {
      this.setWrittenCharsCount()
      if (this.isFieldEmpty()) {
        this.$emit('input', '')
      } else {
        this.$emit('input', this.getHtml())
      }
    },
    onClick (e) {
      if (e.target === this.editor.editor) {
        this.firstTouchPerformed = true
        this.focused = true
        this.$emit('focus', e)
        if (this.error.$reset) {
          this.error.$reset()
        }
      }
    },
    onBlur () {
      if (this.focused) {
        if (this.error.$touch) {
          this.error.$touch()
        }
      }
      this.focused = false
      this.$emit('blur')
    },
    getHtml () {
      return this.editor.getEditorValue()
    },
    getText () {
      return this.editor.editor.textContent
    },
    isFieldEmpty () {
      return (this.getText().length <= 1 && this.getText().indexOf('\n') > -1) ||
        this.getText() === '' ||
        (this.getText().length === 1 && this.getText().charCodeAt(0) === 65279)
    },
    generateErrors (errorParams = []) {
      const errors = errorMessages
      const matchedErrors = errors.filter(m => errorParams.indexOf(m.type) > -1)
      const messages = matchedErrors.length ? matchedErrors.map(e => e.message) : []
      return messages
    },
    setWrittenCharsCount () {
      /** Set the length of the raw text (without html tags and special character with code 65279 (this characters is inserted by editor automatically) **/
      this.charsWritten = this.getText().length - (this.getText().match(new RegExp(String.fromCharCode(65279), "g")) || []).length
    },
    updateEditorValue (value) {
      if (!isNil(value)) {
        this.editor.setEditorValue(value)
      }
    }

  },
  mounted () {
    /** Create editor instance**/
    this.editor = new Jodit(this.$refs.editor, {
      minHeight: this.height,
      placeholder: this.placeholder
    })
    /** Set initial value (which passed through v-model) **/
    this.updateEditorValue(this.value)
    this.setWrittenCharsCount()
  }
}
</script>

<style lang="scss" scoped>
.jodit-editor {
  &__wrapper {
    width: 100%;
    position: relative;
    margin: 20px 0;

    /deep/ .jodit_placeholder {
      padding: 10px 16px !important;
      font-size: 16px !important;
    }

    /deep/ .jodit_wysiwyg {
      padding: 10px 16px 35px !important;
    }

    /deep/ .jodit_wysiwyg p {
      min-height: 21px;
    }
  }

  &__container {
    position: relative;
  }

  &__error {
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    margin-top: 10px;
    color: #ff5252;
  }

  &__message {
    position: absolute;
    right: 22px;
    bottom: 0;
    padding: 5px;
    font-size: 15px;
    font-weight: 500;
    color: rgba(107, 107, 109, 0.78);
    margin-bottom: 10px;
    background: #f9f9f9;
    border-radius: 5px;

    &--invalid {
      color: rgba(255, 0, 0, 0.8);
    }

    &--valid {
      color: rgb(102, 187, 106);
    }

    & > div {
      color: inherit;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/shared/ContentEditor/index.vue